import React from 'react';
import CustomAvatar from '../../CustomComponents/CustomAvatar';
import { StatusDiv } from '../Billing/PaymentLog/styledComponents';
import { AssignButton } from '../JobList/styledComponents';
import {
  FacilityStatusDiv,
  StyledFacilityBlocks,
  StyledFacilityContainer
} from './styledComponents';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

export const createAuditColumns = (tab, handleStartButtonClick, { showStrike = true, excludeColumns = [] } = {}) => {
  switch (tab) {
    case 1:
      return ([
        {
          id: 'jobNumber',
          accessorKey: 'jobNumber',
          header: 'Job Number',
          size: 130,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'author',
          accessorKey: 'author',
          header: 'Author',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'primaryCarePhysician',
          accessorKey: 'primaryCarePhysician',
          header: 'Primary Care Physician',
          size: 210,
          minSize: 80,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => {
            const val = getValue();
            if (!val) return 'N/A';
            return (
              <span title={val}>
                {val.length > 20 ? val.slice(0, 20) + '...' : val}
              </span>
            );
          }
        },
        {
          id: 'patientName',
          accessorKey: 'patientName',
          header: 'Patient Name',
          size: 180,
          minSize: 100,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'submissionTime',
          accessorFn: row =>
            row.submissionTime
              ? formatDateTime(row.submissionTime)
              : 'N/A',
          header: 'Submission Time',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.submissionTime;
            const b = rowB.original.submissionTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'effectiveDate',
          accessorFn: row =>
            row.effectiveDate
              ? formatDateTime(row.effectiveDate)
              : 'N/A',
          header: 'Encounter Date',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.effectiveDate
              ? new Date(rowA.original.effectiveDate).getTime()
              : 0;
            const b = rowB.original.effectiveDate
              ? new Date(rowB.original.effectiveDate).getTime()
              : 0;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'facilityName',
          accessorFn: row => row.facility?.facilityName,
          header: 'Facility',
          size: 180,
          minSize: 100,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => {
            const val = getValue();
            if (!val) return 'N/A';
            return (
              <StyledFacilityContainer>
                <StyledFacilityBlocks>{val}</StyledFacilityBlocks>
              </StyledFacilityContainer>
            );
          }
        },
        {
          id: 'status',
          accessorKey: 'status',
          header: 'Status',
          size: 130,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || '--'
        },
        {
          id: 'workType',
          accessorKey: 'workType',
          header: 'Work Type',
          size: 160,
          minSize: 100,
          maxSize: 350,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'dictationTemplateName',
          accessorFn: row => row.dictationTemplate?.templateName,
          header: 'Template',
          size: 150,
          minSize: 100,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'billerName',
          accessorFn: row => row.biller?.fullName,
          header: 'Biller',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'billStatus',
          accessorFn: row => (row.billStatus ? 'Billed' : 'Not Billed'),
          header: 'Bill Status',
          size: 120,
          minSize: 80,
          maxSize: 250,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'reviewerName',
          accessorFn: row => row.reviewerInProgress?.fullName,
          header: 'Current Reviewer',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'scribeName',
          accessorFn: row => row.scribe?.fullName,
          header: 'Scribe',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'currentLevelOfReview',
          accessorKey: 'currentLevelOfReview',
          header: 'Current Review Level',
          size: 130,
          minSize: 80,
          maxSize: 250,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'submissionToReviewerLevel1Time',
          accessorFn: row =>
            row.submissionToReviewerLevel1Time
              ? formatDateTime(row.submissionToReviewerLevel1Time)
              : 'N/A',
          header: 'Sent to Reviewer',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.submissionToReviewerLevel1Time;
            const b = rowB.original.submissionToReviewerLevel1Time;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'rejectedByReviewerTime',
          accessorFn: row =>
            row.rejectedByReviewerTime
              ? formatDateTime(row.rejectedByReviewerTime)
              : 'N/A',
          header: 'Reviewer Rejection Time',
          size: 200,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.rejectedByReviewerTime;
            const b = rowB.original.rejectedByReviewerTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'billedTime',
          accessorFn: row =>
            row.billedTime
              ? formatDateTime(row.billedTime)
              : 'N/A',
          header: 'Billed Time',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.billedTime;
            const b = rowB.original.billedTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'emrSubmissionTime',
          accessorFn: row =>
            row.emrSubmissionTime
              ? formatDateTime(row.emrSubmissionTime)
              : 'N/A',
          header: 'EMR Submission Time',
          size: 200,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.emrSubmissionTime;
            const b = rowB.original.emrSubmissionTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        // {
        //   id: 'cptCodesInContent',
        //   accessorFn: row => {
        //     const codes = row.cptCodesInContent;
        //     if (!codes || codes.length === 0) return 'N/A';
        //     return codes.map(c => c.cptCode).filter(Boolean).join(', ') || 'N/A';
        //   },
        //   header: 'CPT Code',
        //   size: 180,
        //   minSize: 100,
        //   maxSize: 400,
        //   enableSorting: true,
        //   filterFn: multiSelectFilter,
        //   cell: ({ getValue }) => getValue()
        // },
        {
          id: 'admissionDate',
          accessorFn: row => {
            const date = row.patientDemographics?.admissionDate;
            if (!date) return 'N/A';
            return formatDate(date);
          },
          header: 'Admission Date',
          size: 160,
          minSize: 100,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.patientDemographics?.admissionDate;
            const b = rowB.original.patientDemographics?.admissionDate;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return new Date(a).getTime() - new Date(b).getTime();
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'mrn',
          accessorFn: row => row.patientDemographics?.medicalRecordNumber,
          header: 'MRN',
          size: 150,
          minSize: 80,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'dischargeDate',
          accessorFn: row => {
            const date = row.patientDemographics?.dischargeDate;
            if (!date) return 'N/A';
            return formatDate(date);
          },
          header: 'Discharge Date',
          size: 160,
          minSize: 100,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.patientDemographics?.dischargeDate;
            const b = rowB.original.patientDemographics?.dischargeDate;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return new Date(a).getTime() - new Date(b).getTime();
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'mipsScore',
          accessorFn: row => row.mips?.score ?? null,
          header: 'MIPS Score',
          size: 120,
          minSize: 80,
          maxSize: 200,
          enableSorting: true,
          filterFn: multiSelectFilter,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.mips?.score ?? -1;
            const b = rowB.original.mips?.score ?? -1;
            return a - b;
          },
          cell: ({ getValue }) => {
            const score = getValue();
            if (score === null || score === undefined) return 'N/A';
            return `${score}/100`;
          }
        },
        ...(showStrike ? [{
          id: 'strike',
          header: 'Strike',
          size: 130,
          minSize: 100,
          maxSize: 200,
          enableSorting: false,
          enableColumnFilter: false,
          enableResizing: false,
          cell: ({ row }) => {
            const record = row.original;
            const userString = localStorage.getItem('user');
            const user = userString ? JSON.parse(userString) : null;
            const canStrike =
              (record.status === 'Completed' || record.status === 'Struck Out') &&
              (record.progressNote !== null || record.practitionerNote !== null) &&
              record.authorId === user?.id;
            if (!canStrike) return null;
            return (
              <AssignButton
                type='primary'
                disabled={record.status === 'Struck Out'}
                onClick={e => {
                  e.stopPropagation();
                  handleStartButtonClick(record.jobId);
                }}
              >
                Strike Out
              </AssignButton>
            );
          }
        }] : [])
      ]).filter(col => !excludeColumns.includes(col.id));

    case 2:
      return [
        {
          id: 'profilePic',
          accessorKey: 'profilePic',
          header: '',
          size: 50,
          minSize: 40,
          maxSize: 80,
          enableSorting: false,
          enableColumnFilter: false,
          enableResizing: false,
          cell: ({ getValue }) => <CustomAvatar url={getValue()} size={35} />
        },
        {
          id: 'fullName',
          accessorKey: 'fullName',
          header: 'Name',
          size: 180,
          minSize: 100,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => {
            const val = getValue();
            if (!val) return 'N/A';
            return (
              <span title={val}>
                {val.length > 20 ? val.slice(0, 20) + '...' : val}
              </span>
            );
          }
        },
        {
          id: 'email',
          accessorKey: 'email',
          header: 'Email',
          size: 220,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'loginTime',
          accessorFn: row =>
            row.loginTime
              ? formatDateTime(row.loginTime)
              : 'N/A',
          header: 'Login',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.loginTime;
            const b = rowB.original.loginTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'userType',
          accessorKey: 'userType',
          header: 'User Type',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'loggedUserIp',
          accessorKey: 'loggedUserIp',
          header: 'IP Address',
          size: 150,
          minSize: 100,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'loggingStatus',
          accessorKey: 'loggingStatus',
          header: 'Status',
          size: 120,
          minSize: 80,
          maxSize: 250,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => {
            const val = getValue();
            return (
              <StatusDiv status={val} audit={true}>
                {val?.toLowerCase()}
              </StatusDiv>
            );
          }
        },
        {
          id: 'logoutTime',
          accessorFn: row => {
            const { logoutTime, loggingStatus } = row;
            if (logoutTime === 0)
              return loggingStatus === 'FAILED' ? '-' : 'Active Session';
            return logoutTime
              ? formatDateTime(logoutTime)
              : 'N/A';
          },
          header: 'Logout',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.logoutTime;
            const b = rowB.original.logoutTime;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        }
      ];

    case 3:
      return [
        {
          id: 'facilityName',
          accessorKey: 'facilityName',
          header: 'Facility Name',
          size: 200,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue() || 'N/A'
        },
        {
          id: 'facilityDisableStatusLastUpdated',
          accessorFn: row =>
            row.facilityDisableStatusLastUpdated
              ? formatDateTime(row.facilityDisableStatusLastUpdated)
              : '--',
          header: 'Last Updated',
          size: 180,
          minSize: 120,
          maxSize: 400,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.facilityDisableStatusLastUpdated;
            const b = rowB.original.facilityDisableStatusLastUpdated;
            if (!a && !b) return 0;
            if (!a) return 1;
            if (!b) return -1;
            return a - b;
          },
          filterFn: multiSelectFilter,
          cell: ({ getValue }) => getValue()
        },
        {
          id: 'disabled',
          accessorFn: row => (row.disabled ? 'Disabled' : 'Enabled'),
          header: 'Facility Status',
          size: 150,
          minSize: 80,
          maxSize: 300,
          enableSorting: true,
          filterFn: multiSelectFilter,
          cell: ({ row }) => (
            <FacilityStatusDiv status={!row.original.disabled}>
              {row.original.disabled ? 'Disabled' : 'Enabled'}
            </FacilityStatusDiv>
          )
        }
      ];

    default:
      return [];
  }
};
